import { useLegalModalStore } from '@common/stores/legal-modal-store';
import { Button } from '@konova/ui/button';
import { Link } from '@konova/ui/link';
import { Modal } from '@konova/ui/modal';
import { Stack } from '@konova/ui/stack';
import { Typography } from '@konova/ui/typography';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { IOrganizationData } from '../../../../types';
import { BrandLogo } from '../brand-logo';
import classes from './imprint-modal.module.scss';

export type ImprintModalProps = {
  /**
   * Data structure representing organization data.
   */
  organizationData?: IOrganizationData | null;
};

export const ImprintModal: FC<ImprintModalProps> = ({ organizationData }) => {
  const { t } = useTranslation();
  const showImprintModal = useLegalModalStore(state => state.showImprintModal);
  const setShow = useLegalModalStore(state => state.setShowImprintModal);
  const showPrivacyModal = useLegalModalStore(state => state.setShowPrivacyModal);

  const atLeastOneOrganisationInfoIsAvailable
    = organizationData?.organization
      || organizationData?.organization_address
      || organizationData?.organization_address2
      || organizationData?.organization_zip
      || organizationData?.organization_city
      || organizationData?.organization_mail
      || organizationData?.organization_website;

  return (
    <Modal
      size="xl"
      open={showImprintModal}
      className={classes.root}
      label={t('components.footer.imprint.title')}
      data-testid="imprint-modal"
      onClose={() => setShow(false)}
      title={(
        <Typography as="h3" variant="h3">
          {t('components.footer.imprint.title')}
        </Typography>
      )}
      footer={(
        <Button data-testid="close" variant="filled" onClick={() => setShow(false)}>
          {t('common.close')}
        </Button>
      )}
    >
      <Typography as="h4" variant="h4">
        {t('components.footer.imprint.responsible')}
      </Typography>

      <Stack direction="column" spacing={3}>
        {atLeastOneOrganisationInfoIsAvailable ? (
          <Stack direction="column" spacing={2}>
            <div>
              {organizationData.organization && (
                <Typography bold as="p" variant="paragraph">
                  {organizationData.organization}
                </Typography>
              )}

              {organizationData.organization_address && (
                <Typography as="p" variant="paragraph">
                  {organizationData.organization_address}
                </Typography>
              )}
              {organizationData.organization_address2 && (
                <Typography as="p" variant="paragraph">
                  {organizationData.organization_address2}
                </Typography>
              )}

              <Typography as="p" variant="paragraph">
                {organizationData.organization_zip}
&nbsp;
                {organizationData.organization_city}
              </Typography>
            </div>

            <Stack direction="column" spacing={0.5}>
              {organizationData.organization_mail && (
                <Link
                  as="a"
                  color="primary"
                  href={`mailto:${organizationData.organization_mail}`}
                >
                  {organizationData.organization_mail}
                </Link>
              )}

              {organizationData.organization_website && (
                <Link
                  as="a"
                  color="primary"
                  href={`${organizationData.organization_website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {organizationData.organization_website}
                </Link>
              )}
            </Stack>
          </Stack>
        ) : (
          <Typography>
            {t('components.footer.imprint.responsible-provider')}
            .
          </Typography>
        )}

        <div color="grey.light" className={classes.solutionInfoBox}>
          <Stack direction="column" spacing={0} className={classes.content}>
            <Typography as="h4" variant="h4" className={classes.solutionInfoTitle}>
              {t('components.footer.imprint.solution')}
            </Typography>
            <Typography>
              {t('components.footer.imprint.solution-info')}
&nbsp;
              <Link
                as="a"
                color="primary"
                href="https://e-mitwirkung.ch"
                target="_blank"
                rel="noopener noreferrer"
              >
                www.e-mitwirkung.ch
              </Link>
              .
            </Typography>
          </Stack>

          <div className={classes.logoWrapper}>
            <BrandLogo />
          </div>
        </div>

        <Stack direction="column" spacing={1}>
          <Typography as="h4" variant="h4">
            {t('common.privacy')}
          </Typography>
          <Typography>
            {t('components.footer.imprint.privacy-text')}
&nbsp;
            <Link
              as="button"
              color="primary"
              data-testid="open-privacy-modal"
              alt={t('common.privacy')}
              onClick={() => showPrivacyModal(true)}
            >
              {t('common.privacy')}
              .
            </Link>
          </Typography>
        </Stack>
      </Stack>
    </Modal>
  );
};
